/**
 *  LYVN · cart drawer trust layer
 *  File: assets/lyvn-cart-trust.js
 *
 *  Three jobs:
 *    1. paint the dispatch countdown in IST
 *    2. handle one-tap add on the recommendation cards
 *    3. spray the drawer when the top reward tier unlocks
 *
 *  Everything else in the trust layer is server-rendered Liquid and repaints
 *  itself, because the theme replaces the drawer HTML on every cart change.
 *
 *  Cut-off hour comes from the snippet via window.lyvnTrustConfig.
 */

(function () {
  if (window.__lyvnCartTrust) return;
  window.__lyvnCartTrust = true;

  function cutoffHour() {
    return (window.lyvnTrustConfig && window.lyvnTrustConfig.cutoffHour) || 13;
  }

  // Shown in place of the live countdown once today's cut-off has passed, so the
  // line reads as a standing offer instead of going blank. Fixed text — it does
  // not count down. See paintCutoff().
  var LATE_WINDOW = '2 hours';

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------------ *
   *  1. dispatch countdown
   * ------------------------------------------------------------------ */

  // 4h 11m -> "4 hours 11 minutes"
  function humanise(ms) {
    // Round up, never below 1. Flooring made the last 59 seconds before the
    // cut-off read "0 minutes", which looks broken rather than urgent — and
    // rounding up is also the honest direction for a deadline.
    var mins = Math.max(1, Math.ceil(ms / 60000));
    var h = Math.floor(mins / 60);
    var m = mins % 60;

    if (h === 0) return m + (m === 1 ? ' minute' : ' minutes');
    if (m === 0) return h + (h === 1 ? ' hour' : ' hours');
    return h + (h === 1 ? ' hour ' : ' hours ') + m + (m === 1 ? ' minute' : ' minutes');
  }

  // Writing textContent is a DOM mutation even when the string is identical.
  // This observer reacts to mutations inside the drawer, so an unguarded write
  // re-triggers it forever — and GoKwik only re-enables its checkout button
  // after 700ms of DOM quiet, which that loop never allows. Write only on a
  // real change.
  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function paintCutoff() {
    var host = document.querySelector('#Cart-Drawer [data-lyvn-cutoff-line]');
    if (!host) return;

    var el = host.querySelector('[data-lyvn-cutoff]');
    var tail = host.querySelector('[data-lyvn-cutoff-tail]');
    if (!el) return;

    // IST, whatever the device timezone is
    var now = new Date();
    var ist = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + (5.5 * 3600000));
    var cutoff = new Date(ist);
    cutoff.setHours(cutoffHour(), 0, 0, 0);
    var diff = cutoff - ist;

    // Sunday — the warehouse is shut, so there is no window to offer at all.
    // data-late hides the "Order within … " copy and shows the tail instead.
    if (ist.getDay() === 0) {
      if (host.getAttribute('data-late') !== 'true') host.setAttribute('data-late', 'true');
      setText(el, '');
      setText(tail, 'Ships on the next working day');
      return;
    }

    // Past today's cut-off. The countdown has nothing left to count, so rather
    // than leaving the line blank it falls back to the fixed window above —
    // same sentence, same red, just not ticking.
    if (diff <= 0) {
      if (host.hasAttribute('data-late')) host.removeAttribute('data-late');
      setText(el, LATE_WINDOW);
      setText(tail, '');
      return;
    }

    if (host.hasAttribute('data-late')) host.removeAttribute('data-late');
    setText(el, humanise(diff));
    setText(tail, '');
  }

  /* ------------------------------------------------------------------ *
   *  2. tier unlock celebration
   * ------------------------------------------------------------------ */

  // How many tiers were unlocked last time we looked. Kept in sessionStorage
  // so it survives a page change: adding an item on a product page and then
  // opening the drawer still counts as crossing the line. -1 means "never
  // measured in this session", which stays quiet — a returning shopper whose
  // cart already qualifies has not just achieved anything.
  var TIER_KEY = 'lyvnTierUnlocked';

  function readTier() {
    try {
      var v = sessionStorage.getItem(TIER_KEY);
      return v === null ? -1 : parseInt(v, 10);
    } catch (e) { return -1; }
  }

  function writeTier(n) {
    try { sessionStorage.setItem(TIER_KEY, String(n)); } catch (e) {}
  }

  var lastUnlocked = readTier();

  function unlockedNodes(bar) {
    return bar.querySelectorAll('.lyvn-fs__node.is-done');
  }

  function newestLabel(bar) {
    var nodes = unlockedNodes(bar);
    if (!nodes.length) return '';
    var el = nodes[nodes.length - 1].querySelector('.lyvn-fs__label');
    return el ? el.textContent.trim() : '';
  }

  function savedAmount() {
    var el = document.querySelector('#Cart-Drawer .lyvn-fs-payable__off');
    if (!el) return '';
    // the Liquid line renders "− Rs.839.40" — drop the minus
    return el.textContent.replace(/^[\s\u2212-]+/, '').trim();
  }

  // reuse the bar's own message so the nudge can never contradict it
  function nextHint(bar) {
    if (bar.getAttribute('data-state') === 'complete') {
      return 'It comes off automatically at checkout — nothing to enter.';
    }
    var msg = bar.querySelector('.lyvn-fs__msg');
    return msg ? msg.textContent.trim() + '.' : '';
  }

  // canvas-confetti is already loaded by cart-drawer.liquid. Its default canvas
  // sits at z-index 100, well under the drawer's 99999, so we hand it our own
  // canvas inside the popup instead.
  function burstConfetti(pop, card) {
    if (typeof window.confetti !== 'function') return null;

    var canvas = pop.querySelector('.lyvn-pop__confetti');
    if (!canvas) return null;

    var shoot = window.confetti.create(canvas, { resize: true });
    var pr = pop.getBoundingClientRect();
    var cr = card.getBoundingClientRect();
    if (!pr.width || !pr.height) return shoot;

    var x = (cr.left + cr.width / 2 - pr.left) / pr.width;
    var yTop = (cr.top - pr.top) / pr.height;
    var yBottom = (cr.bottom - pr.top) / pr.height;
    var colors = ['#d63030', '#F26A6A', '#F2E9E6', '#14110F', '#D9A441'];

    shoot({
      particleCount: 55, spread: 76, startVelocity: 32, scalar: .85,
      ticks: 190, origin: { x: x, y: yTop }, colors: colors
    });

    setTimeout(function () {
      shoot({
        particleCount: 45, spread: 90, startVelocity: 26, scalar: .8,
        ticks: 170, origin: { x: x, y: yBottom }, colors: colors
      });
    }, 180);

    return shoot;
  }

  function closePop(pop) {
    if (!pop || !pop.parentNode) return;
    if (pop.__shoot && pop.__shoot.reset) {
      try { pop.__shoot.reset(); } catch (e) {}
    }
    pop.setAttribute('data-closing', 'true');
    setTimeout(function () {
      if (pop.parentNode) pop.parentNode.removeChild(pop);
    }, 260);
  }

  function celebrate(label, saving, hint) {
    var host = document.getElementById('Cart-Drawer');
    if (!host) return;

    var old = host.querySelector('.lyvn-pop');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var pop = document.createElement('div');
    pop.className = 'lyvn-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-live', 'polite');

    pop.innerHTML =
      '<div class="lyvn-pop__backdrop" data-lyvn-pop-dismiss></div>' +
      '<canvas class="lyvn-pop__confetti" aria-hidden="true"></canvas>' +
      '<div class="lyvn-pop__card">' +
        '<span class="lyvn-pop__rays" aria-hidden="true"></span>' +
        '<button type="button" class="lyvn-pop__close" aria-label="Close" data-lyvn-pop-dismiss>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
            '<path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        '<span class="lyvn-pop__badge" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M4 12.5l5 5L20 6.5"/></svg>' +
        '</span>' +
        '<p class="lyvn-pop__title">' + (label || 'Reward') + ' unlocked</p>' +
        (saving
          ? '<p class="lyvn-pop__amount">You save <b>' + saving + '</b></p>'
          : '') +
        (hint ? '<p class="lyvn-pop__sub">' + hint + '</p>' : '') +
      '</div>';

    host.appendChild(pop);

    pop.addEventListener('click', function (e) {
      if (e.target.closest('[data-lyvn-pop-dismiss]')) closePop(pop);
    });

    setTimeout(function () {
      pop.__shoot = burstConfetti(pop, pop.querySelector('.lyvn-pop__card'));
    }, 260);

    setTimeout(function () { closePop(pop); }, 5200);
  }

  function drawerIsOpen() {
    var d = document.getElementById('Cart-Drawer');
    return !!d && d.classList.contains('active');
  }

  // app.js locks page scroll for the search drawer and the mobile menu
  // (body.overflow-hidden) but the cart drawer's open() only adds `open-cc`,
  // so the page behind keeps scrolling. Mirror the theme's convention from here
  // rather than editing app.js, which a theme update would overwrite.
  //
  // The class alone is enough on desktop. It is not enough on iOS Safari, which
  // treats `overflow: hidden` on body as a hint and keeps scrolling the page
  // anyway once a touch drag is in flight — so the shopper gets two scrolls at
  // once. The only lock iOS honours is taking body out of flow, which means
  // remembering the scroll offset and putting it back on close.
  var lockedAt = null;

  // Search and the mobile menu use the same body class. If one of them is open
  // when the cart drawer closes, its lock has to survive.
  function otherPanelOpen() {
    var panels = document.querySelectorAll('.side-panel.active');
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].id !== 'Cart-Drawer') return true;
    }
    var toggle = document.querySelector('.mobile-toggle-wrapper');
    return !!(toggle && toggle.classList.contains('active'));
  }

  // Taking body out of flow also takes the sticky header out of flow, which on
  // desktop makes it jump the moment the drawer opens. Desktop does not need it
  // — the class works there — so the hard lock is mobile only.
  function needsHardLock() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  function lockPage() {
    // The observer fires many times while the drawer is open. Re-reading the
    // offset on a later pass would read 0 — body is already fixed by then — and
    // close would dump the shopper at the top of the page.
    if (lockedAt !== null) return;

    lockedAt = window.pageYOffset || document.documentElement.scrollTop || 0;

    var b = document.body;
    b.classList.add('overflow-hidden');
    if (!needsHardLock()) return;

    b.style.position = 'fixed';
    b.style.top = (-lockedAt) + 'px';
    b.style.left = '0';
    b.style.width = '100%';
  }

  function unlockPage() {
    if (lockedAt === null) return;

    var y = lockedAt;
    lockedAt = null;

    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.width = '';
    if (!otherPanelOpen()) b.classList.remove('overflow-hidden');

    // Dropping `position: fixed` snaps the document to offset 0, so the offset
    // has to go back in the same frame or the page visibly jumps to the top.
    window.scrollTo(0, y);
  }

  function syncBodyLock() {
    if (drawerIsOpen()) { lockPage(); return; }
    unlockPage();
  }

  function checkUnlock() {
    var bar = document.querySelector('#Cart-Drawer .lyvn-fs');

    // No bar at all means an empty cart — the snippet only renders inside
    // {% if cart != empty %}. Nothing is unlocked, and that IS the baseline:
    // without recording it the first item added would look like the start of
    // the session and its unlock would be swallowed.
    if (!bar) {
      if (lastUnlocked !== 0) { lastUnlocked = 0; writeTier(0); }
      return;
    }

    var count = unlockedNodes(bar).length;

    // First measurement of the session. Baseline it whether or not the drawer
    // is open — otherwise the very first unlock gets mistaken for the baseline
    // and swallowed.
    if (lastUnlocked === -1) { lastUnlocked = count; writeTier(count); return; }

    // same tier, or the cart dropped back down
    if (count <= lastUnlocked) { lastUnlocked = count; writeTier(count); return; }

    // A new tier. Nothing to celebrate into a closed drawer, so leave the
    // marker alone and let the popup wait until the shopper opens it.
    if (!drawerIsOpen()) return;

    lastUnlocked = count;
    writeTier(count);
    if (reducedMotion()) return;
    celebrate(newestLabel(bar), savedAmount(), nextHint(bar));
  }

  /* ------------------------------------------------------------------ *
   *  3. one-tap add
   * ------------------------------------------------------------------ */

  function refreshDrawer() {
    return fetch(window.Shopify.routes.root + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) { window.location.reload(); return; }

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('#Cart-Drawer .side-panel-inner');
        var live = document.querySelector('#Cart-Drawer .side-panel-inner');

        if (fresh && live) {
          live.innerHTML = fresh.innerHTML;
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          paintCutoff();
          checkUnlock();
        }
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#Cart-Drawer .lyvn-ct-quickadd[data-variant-id]');
    if (!btn) return;
    e.preventDefault();

    var id = btn.getAttribute('data-variant-id');
    btn.setAttribute('data-state', 'loading');

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        btn.setAttribute('data-state', 'done');
        btn.textContent = 'Added';
        return refreshDrawer();
      })
      .catch(function () {
        btn.setAttribute('data-state', '');
        btn.textContent = 'Add';
      });
  });

  /* ------------------------------------------------------------------ *
   *  size picker on "You may also like" cards
   *  desktop: shows on hover · touch screens: always visible
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var size = e.target.closest('#Cart-Drawer .lyvn-ct-size[data-variant-id]');
    if (!size) return;
    e.preventDefault();
    if (size.getAttribute('data-state') === 'loading') return;

    var bar = size.closest('.lyvn-ct-sizes__bar');
    if (bar) bar.classList.add('is-busy');
    size.setAttribute('data-state', 'loading');

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(size.getAttribute('data-variant-id')), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        size.setAttribute('data-state', 'done');
        return refreshDrawer();
      })
      .catch(function () {
        size.setAttribute('data-state', 'error');
        if (bar) bar.classList.remove('is-busy');
        setTimeout(function () { size.removeAttribute('data-state'); }, 1600);
      });
  });

  /* ------------------------------------------------------------------ *
   *  minus at quantity 1 removes the item
   *  (the theme's quantity box stops at 1, so "−" did nothing but flash)
   *  Runs in the capture phase so it beats the theme's own minus handler.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var minus = e.target.closest('#Cart-Drawer quantity-selector.cart-update .minus');
    if (!minus) return;
    var selector = minus.closest('quantity-selector');
    var input = selector && selector.querySelector('input.qty');
    if (!input) return;
    var qty = parseInt(input.value, 10) || 0;
    var step = parseInt(input.getAttribute('step'), 10) || 1;
    if (qty - step >= 1) return;               // normal decrease, leave it to the theme

    e.preventDefault();
    e.stopImmediatePropagation();

    var line = input.getAttribute('data-index');
    var drawer = document.getElementById('Cart-Drawer');
    if (drawer && typeof drawer.updateQuantity === 'function' && line) {
      drawer.updateQuantity(line, '0');
      return;
    }
    // fallback: use the item's own remove (trash) link
    var row = selector.parentElement;
    var remove = row && row.querySelector('a.remove');
    if (remove) remove.click();
  }, true);

  /* ------------------------------------------------------------------ *
   *  wiring
   * ------------------------------------------------------------------ */

  // The theme swaps drawer HTML on every cart change — repaint once it settles
  var drawer = document.getElementById('Cart-Drawer');
  if (drawer) {
    new MutationObserver(function () {
      clearTimeout(window.__lyvnCutoffTimer);
      window.__lyvnCutoffTimer = setTimeout(function () {
        paintCutoff();
        checkUnlock();
      }, 250);
      syncBodyLock();
    }).observe(drawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']   // so opening the drawer is a trigger too
    });
  }

  // Coming back from checkout, iOS restores the page from the back-forward
  // cache with the DOM exactly as it was left — body still fixed, drawer shut.
  // Nothing mutates, so the observer never fires and the page stays frozen.
  window.addEventListener('pageshow', syncBodyLock);

  paintCutoff();
  checkUnlock();
  syncBodyLock();
  setInterval(paintCutoff, 60000);
})();